import { groupBy } from 'lodash';
import {
  ContributionClassesAndRolesDDL,
  ContributionClassName,
  ContributionClassRole,
  ContributionClassRolesDDL,
} from 'types/contribution';
import {
  idFieldInfoMap,
  isniFieldInfoMap,
  roleFieldsInfoMap,
} from 'app/components/Song/Contributions/ContributionForm/RoleFields/constants';
import { PerformerRoleType } from '../types';
import {
  DetailField,
  IdFieldsInfo,
  IsniFieldName,
  IsniFieldsInfo,
  RoleFieldsInfo,
} from './types';

export const getOptions = (roles: ContributionClassRolesDDL) =>
  Object.values(roles)
    .map((el) => ({
      title: el.name,
      value: el.key,
    }))
    .sort((a, b) => a.title.localeCompare(b.title));

export const getGroupedOptions = (
  parent: string,
  contributionClassesAndRoles: ContributionClassesAndRolesDDL
) => {
  const getGroups = (parent: PerformerRoleType) =>
    Object.values(contributionClassesAndRoles.classRoles).filter(
      (el) => el.notSelectable && el.parent === parent
    );

  const sortByGroup = (
    groups: ContributionClassRole[],
    roles: ContributionClassRolesDDL
  ) =>
    groups.length
      ? groups
          .map((group) =>
            Object.values(roles)
              .filter((role) => role.parent === group.key)
              .sort((a, b) => a.name.localeCompare(b.name))
          )
          .flat()
          .map((el) => {
            const group = groups.find((group) => group.key === el.parent);
            return { title: el.name, value: el.key, group: group?.name };
          })
      : getOptions(roles);

  if (
    parent === PerformerRoleType.VOCAL ||
    parent === PerformerRoleType.VOCALIST
  ) {
    const groupsVocal = getGroups(PerformerRoleType.VOCAL);
    const groupsVocalist = getGroups(PerformerRoleType.VOCALIST);
    const groups = groupsVocal.length ? groupsVocal : groupsVocalist;
    return sortByGroup(groups, contributionClassesAndRoles.performanceVocals);
  }
  if (
    parent === PerformerRoleType.GROUP ||
    parent === PerformerRoleType.ENSEMBLE
  ) {
    const groupsGroup = getGroups(PerformerRoleType.GROUP);
    const groupsEnsemble = getGroups(PerformerRoleType.ENSEMBLE);
    const groups = groupsGroup.length ? groupsGroup : groupsEnsemble;
    return sortByGroup(groups, contributionClassesAndRoles.performanceGroups);
  }
  if (parent === PerformerRoleType.MUSICIAN) {
    const groups = getGroups(PerformerRoleType.MUSICIAN);
    return sortByGroup(
      groups,
      contributionClassesAndRoles.performanceInstruments
    );
  }
  return [];
};

export const getRoleFieldsInfo = (
  contributionClassName: ContributionClassName,
  contributionClassesAndRolesDDL: ContributionClassesAndRolesDDL
): RoleFieldsInfo | undefined =>
  roleFieldsInfoMap(contributionClassesAndRolesDDL)[contributionClassName];

export const getIDFieldInfo = (
  contributionClassName: ContributionClassName
): IdFieldsInfo | undefined =>
  idFieldInfoMap[contributionClassName] as IdFieldsInfo;

export const getIsniFieldInfo = (
  contributionClassName: ContributionClassName
): IsniFieldsInfo | undefined => {
  const isniFieldName: IsniFieldName | undefined = isniFieldInfoMap[
    contributionClassName
  ] as IsniFieldName;

  if (!isniFieldName) {
    return undefined;
  }

  return {
    isniFieldLabel: 'Contributor ID (ISNI)',
    isniFieldName,
    tooltip: 'International Standard Name Identifier (ISNI)',
  };
};

export const formatGroupedOptions = (detailField: DetailField) => {
  const grouped = groupBy(detailField?.options, 'group');

  return Object.keys(grouped).map((key: string) => {
    return { label: key, options: grouped[key] };
  });
};
